(() => {
  'use strict';

  const VERSION = '2.0.0';
  const CACHE_KEY = 'tpv20WeatherPilotCacheV2';
  const ALLOWED_COUNTRIES = Object.freeze({
    de: 'Deutschland',
    dk: 'Dänemark',
    nl: 'Niederlande',
    be: 'Belgien',
    lu: 'Luxemburg',
    fr: 'Frankreich',
    ch: 'Schweiz',
    at: 'Österreich',
    cz: 'Tschechien',
    pl: 'Polen'
  });
  const ALLOWED_CODES = Object.freeze(Object.keys(ALLOWED_COUNTRIES));
  const ALLOWED_CODES_PARAM = ALLOWED_CODES.join(',');

  const DEFAULTS = Object.freeze({
    enabled: true,
    geocoder: 'nominatim',
    geocodeEndpoint: 'https://nominatim.openstreetmap.org/search',
    weatherEndpoint: 'https://api.brightsky.dev/weather',
    openMeteoEndpoint: 'https://api.open-meteo.com/v1/forecast',
    requestTimeoutMs: 8000,
    forecastCacheMinutes: 30,
    geocodeCacheDays: 30
  });

  const states = new WeakMap();
  let lastNominatimRequestAt = 0;

  const now = () => Date.now();
  const normalizePlace = value => String(value || '').trim().replace(/\s+/g, ' ');
  const toNumber = value => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  function parseRootConfig(root) {
    const node = root.querySelector('[data-tpv20-config]');
    if (!node) return { ...DEFAULTS };
    try {
      const config = JSON.parse(node.textContent || '{}');
      return { ...DEFAULTS, ...(config.weatherPilot || {}) };
    } catch (error) {
      console.warn('[TPV20 WetterPilot] Konfiguration konnte nicht gelesen werden.', error);
      return { ...DEFAULTS, enabled: false };
    }
  }

  function readCache() {
    try {
      const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      return {
        geo: parsed.geo && typeof parsed.geo === 'object' ? parsed.geo : {},
        weather: parsed.weather && typeof parsed.weather === 'object' ? parsed.weather : {},
        lastPlace: typeof parsed.lastPlace === 'string' ? parsed.lastPlace : ''
      };
    } catch (_) {
      return { geo: {}, weather: {}, lastPlace: '' };
    }
  }

  function writeCache(cache) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (_) {
      // Der WetterPilot funktioniert auch ohne lokalen Cache.
    }
  }

  function pruneCache(cache) {
    const stamp = now();
    ['geo', 'weather'].forEach(bucket => {
      Object.keys(cache[bucket] || {}).forEach(key => {
        if (!cache[bucket][key]?.expiresAt || cache[bucket][key].expiresAt <= stamp) delete cache[bucket][key];
      });
    });
    return cache;
  }

  function cacheGet(bucket, key) {
    const cache = pruneCache(readCache());
    const entry = cache[bucket]?.[key];
    if (!entry || entry.expiresAt <= now()) return null;
    return entry.value ?? null;
  }

  function cacheSet(bucket, key, value, ttlMs) {
    const cache = pruneCache(readCache());
    cache[bucket] = cache[bucket] || {};
    cache[bucket][key] = { expiresAt: now() + ttlMs, value };
    writeCache(cache);
  }

  function setLastPlace(value) {
    const cache = pruneCache(readCache());
    cache.lastPlace = normalizePlace(value);
    writeCache(cache);
  }

  function getLastPlace() {
    return readCache().lastPlace || '';
  }

  async function fetchJson(url, timeoutMs) {
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => controller.abort(),
      Math.max(1500, Number(timeoutMs) || DEFAULTS.requestTimeoutMs)
    );
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
        referrerPolicy: 'strict-origin-when-cross-origin',
        credentials: 'omit'
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function respectNominatimRateLimit() {
    const minimumGapMs = 1200;
    const waitMs = lastNominatimRequestAt + minimumGapMs - now();
    if (waitMs > 0) await new Promise(resolve => window.setTimeout(resolve, waitMs));
    lastNominatimRequestAt = now();
  }

  async function geocode(place, config) {
    const query = normalizePlace(place);
    const key = `${ALLOWED_CODES_PARAM}|${query.toLowerCase()}`;
    const cached = cacheGet('geo', key);
    if (cached) return cached;

    if (config.geocoder !== 'nominatim') throw new Error('UNSUPPORTED_GEOCODER');
    await respectNominatimRateLimit();

    const url = new URL(config.geocodeEndpoint || DEFAULTS.geocodeEndpoint);
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('limit', '5');
    url.searchParams.set('countrycodes', ALLOWED_CODES_PARAM);
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('accept-language', 'de');

    const data = await fetchJson(url.toString(), config.requestTimeoutMs);
    if (!Array.isArray(data) || !data.length) return null;

    const first = data.find(entry => {
      const code = String(entry?.address?.country_code || '').toLowerCase();
      return ALLOWED_CODES.includes(code) && toNumber(entry?.lat) !== null && toNumber(entry?.lon) !== null;
    });
    if (!first) return null;

    const lat = toNumber(first.lat);
    const lon = toNumber(first.lon);
    if (lat === null || lon === null) return null;

    const address = first.address || {};
    const countryCode = String(address.country_code || '').toLowerCase();
    if (!ALLOWED_CODES.includes(countryCode)) return null;

    const city = address.city || address.town || address.village || address.municipality || address.county || '';
    const postcode = address.postcode || '';
    const compact = [postcode, city].filter(Boolean).join(' ').trim();
    const countryName = ALLOWED_COUNTRIES[countryCode] || address.country || '';
    const compactLabel = [compact || first.display_name || query, countryCode === 'de' ? '' : countryName]
      .filter(Boolean)
      .join(' · ');

    const value = {
      lat,
      lon,
      label: compactLabel,
      displayName: first.display_name || query,
      countryCode,
      countryName
    };

    cacheSet(
      'geo',
      key,
      value,
      Math.max(1, Number(config.geocodeCacheDays) || DEFAULTS.geocodeCacheDays) * 86400000
    );
    return value;
  }

  function berlinDate(date, time) {
    if (!date) return null;
    try {
      const engine = window.TPV20BestellpilotEngine;
      if (engine?.createBerlinDateTime) {
        const result = engine.createBerlinDateTime(date, time || '18:00');
        if (result instanceof Date && Number.isFinite(result.getTime())) return result;
      }
    } catch (_) {}

    const fallback = new Date(`${date}T${time || '18:00'}:00+02:00`);
    return Number.isFinite(fallback.getTime()) ? fallback : null;
  }

  function pickClosestWeather(records, date, time) {
    if (!Array.isArray(records) || !records.length) return null;
    const target = berlinDate(date, time);
    if (!target) return null;

    let best = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    records.forEach(record => {
      const stamp = new Date(record?.timestamp || '');
      const temperature = toNumber(record?.temperature);
      if (!Number.isFinite(stamp.getTime()) || temperature === null) return;
      const distance = Math.abs(stamp.getTime() - target.getTime());
      if (distance < bestDistance) {
        best = record;
        bestDistance = distance;
      }
    });
    return best;
  }

  async function fetchBrightSky(location, date, time, config) {
    const url = new URL(config.weatherEndpoint || DEFAULTS.weatherEndpoint);
    url.searchParams.set('lat', String(location.lat));
    url.searchParams.set('lon', String(location.lon));
    url.searchParams.set('date', date);
    url.searchParams.set('last_date', date);
    url.searchParams.set('tz', 'Europe/Berlin');

    const data = await fetchJson(url.toString(), config.requestTimeoutMs);
    const weather = Array.isArray(data?.weather) ? data.weather : [];
    const selected = pickClosestWeather(weather, date, time);
    return selected ? { ...selected, source: 'DWD' } : null;
  }

  function openMeteoCondition(code) {
    const value = Number(code);
    if (value === 0) return 'dry';
    if ([1, 2, 3].includes(value)) return 'cloudy';
    if ([45, 48].includes(value)) return 'fog';
    if ((value >= 51 && value <= 67) || (value >= 80 && value <= 82)) return 'rain';
    if ((value >= 71 && value <= 77) || [85, 86].includes(value)) return 'snow';
    if (value >= 95 && value <= 99) return 'thunderstorm';
    return '';
  }

  async function fetchOpenMeteo(location, date, time, config) {
    const url = new URL(config.openMeteoEndpoint || DEFAULTS.openMeteoEndpoint);
    url.searchParams.set('latitude', String(location.lat));
    url.searchParams.set('longitude', String(location.lon));
    url.searchParams.set('hourly', 'temperature_2m,weather_code');
    url.searchParams.set('timezone', 'Europe/Berlin');
    url.searchParams.set('start_date', date);
    url.searchParams.set('end_date', date);

    const data = await fetchJson(url.toString(), config.requestTimeoutMs);
    const times = Array.isArray(data?.hourly?.time) ? data.hourly.time : [];
    const temperatures = Array.isArray(data?.hourly?.temperature_2m) ? data.hourly.temperature_2m : [];
    const codes = Array.isArray(data?.hourly?.weather_code) ? data.hourly.weather_code : [];
    const records = times.map((stamp, index) => ({
      timestamp: stamp,
      temperature: temperatures[index],
      condition: openMeteoCondition(codes[index])
    }));
    const selected = pickClosestWeather(records, date, time);
    return selected ? { ...selected, source: 'Open-Meteo' } : null;
  }

  async function fetchWeather(location, date, time, config) {
    const normalizedTime = time || '18:00';
    const key = `${location.countryCode}|${Number(location.lat).toFixed(3)}|${Number(location.lon).toFixed(3)}|${date}|${normalizedTime}`;
    const cached = cacheGet('weather', key);
    if (cached) return cached;

    let weather = null;
    if (location.countryCode === 'de') {
      try {
        weather = await fetchBrightSky(location, date, normalizedTime, config);
      } catch (error) {
        console.warn(`[TPV20 WetterPilot ${VERSION}] DWD/Bright Sky nicht verfügbar, Open-Meteo-Fallback wird genutzt.`, error);
      }
    }

    if (!weather) weather = await fetchOpenMeteo(location, date, normalizedTime, config);
    if (!weather) return null;

    cacheSet(
      'weather',
      key,
      weather,
      Math.max(1, Number(config.forecastCacheMinutes) || DEFAULTS.forecastCacheMinutes) * 60000
    );
    return weather;
  }

  function conditionLabel(value) {
    const labels = {
      dry: 'trocken',
      cloudy: 'bewölkt',
      fog: 'Nebel',
      rain: 'Regen',
      sleet: 'Schneeregen',
      snow: 'Schnee',
      hail: 'Hagel',
      thunderstorm: 'Gewitter'
    };
    return labels[String(value || '').toLowerCase()] || '';
  }

  function formatTarget(date, time) {
    const target = berlinDate(date, time);
    if (!target) return [date, time].filter(Boolean).join(' · ');
    try {
      return new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(target).replace(',', ' ·');
    } catch (_) {
      return `${date} · ${time || '18:00'}`;
    }
  }

  function isPlannerActive(root) {
    const selected = root.querySelector('[data-tpv20-mode][aria-selected="true"]');
    if (selected) return selected.dataset.tpv20Mode === 'planner';
    const planner = root.querySelector('[data-tpv20-mode="planner"]');
    if (planner?.classList.contains('is-active') || planner?.classList.contains('active')) return true;
    return root.dataset.tpv20DefaultMode === 'planner' && !root.querySelector('[data-tpv20-mode].is-active,[data-tpv20-mode].active');
  }

  function syncTiming(root, state) {
    const date = root.querySelector('[data-tpv20-field="date"]')?.value;
    const time = root.querySelector('[data-tpv20-field="time"]')?.value;
    if (date) state.targetDate = date;
    if (time) state.targetTime = time;
  }

  function findTemperatureInput(root) {
    return root.querySelector('.tpv20-review-card:not([hidden]) [data-tpv20-field="temp"]') ||
      root.querySelector('[data-tpv20-mode-host]:not([hidden]) [data-tpv20-field="temp"]') ||
      root.querySelector('[data-tpv20-field="temp"]');
  }

  function setStatus(widget, text, state = 'idle') {
    const status = widget?.querySelector('[data-tpv20-weatherpilot-status]');
    if (!status) return;
    status.dataset.state = state;
    status.textContent = text || '';
  }

  function clearTemperatureHighlight(input) {
    if (!input) return;
    input.classList.remove('tpv20-temp--weather-applied');
    input.removeAttribute('data-tpv20-weather-applied');
    const field = input.closest('.tpv20-field') || input.parentElement;
    field?.querySelector('[data-tpv20-weatherpilot-applied-note]')?.remove();
  }

  function markTemperatureApplied(input) {
    if (!input) return;
    input.classList.add('tpv20-temp--weather-applied');
    input.setAttribute('data-tpv20-weather-applied', 'true');
    const field = input.closest('.tpv20-field') || input.parentElement;
    if (!field) return;

    let note = field.querySelector('[data-tpv20-weatherpilot-applied-note]');
    if (!note) {
      note = document.createElement('span');
      note.className = 'tpv20-weatherpilot__applied-note';
      note.dataset.tpv20WeatherpilotAppliedNote = 'true';
      note.setAttribute('aria-live', 'polite');
      field.appendChild(note);
    }
    note.textContent = '✓ WetterPilot-Wert übernommen';
  }

  function createWidget(root, state, temperatureInput) {
    const field = temperatureInput.closest('.tpv20-field') || temperatureInput.parentElement;
    if (!field?.parentNode) return null;

    const widget = document.createElement('div');
    widget.className = 'tpv20-weatherpilot';
    widget.dataset.tpv20WeatherpilotWidget = 'true';
    widget.innerHTML = `
      <div class="tpv20-weatherpilot__head">
        <span><strong>WetterPilot</strong><span class="tpv20-weatherpilot__optional">Optional</span><small>Temperatur automatisch übernehmen</small></span>
        <span class="tpv20-weatherpilot__source" data-tpv20-weatherpilot-source>DWD / Open-Meteo</span>
      </div>
      <div class="tpv20-weatherpilot__row">
        <label class="tpv20-weatherpilot__place">
          <span>PLZ / Ort</span>
          <input type="search" inputmode="search" autocomplete="postal-code" placeholder="z. B. 56068 Koblenz oder 1010 Wien" data-tpv20-weatherpilot-place>
        </label>
        <button type="button" class="tpv20-weatherpilot__button" data-tpv20-weatherpilot-fetch>Wetter abrufen</button>
      </div>
      <details class="tpv20-weatherpilot__details">
        <summary>Was macht der WetterPilot?</summary>
        <div class="tpv20-weatherpilot__explain">
          Der WetterPilot übernimmt die erwartete Außentemperatur für Ort und Einsatzzeit. Unterstützt werden Deutschland sowie alle direkt angrenzenden Länder. Je wärmer die Umgebung, desto schneller sublimiert Trockeneis. Der Wert bleibt jederzeit manuell änderbar.
        </div>
      </details>
      <div class="tpv20-weatherpilot__status" data-tpv20-weatherpilot-status data-state="idle" aria-live="polite"></div>
      <small class="tpv20-weatherpilot__meta">Deutschland + direkte Nachbarländer · DWD/Bright Sky · Open-Meteo · Geocoding © OpenStreetMap-Mitwirkende</small>
    `;

    field.insertAdjacentElement('beforebegin', widget);

    const placeInput = widget.querySelector('[data-tpv20-weatherpilot-place]');
    placeInput.value = state.lastPlace || getLastPlace();

    const run = () => updateFromWeather(root, state, widget);
    widget.querySelector('[data-tpv20-weatherpilot-fetch]')?.addEventListener('click', run);
    placeInput.addEventListener('keydown', event => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      run();
    });

    state.widget = widget;
    state.temperatureInput = temperatureInput;
    return widget;
  }

  async function updateFromWeather(root, state, widget) {
    syncTiming(root, state);
    const config = state.config;
    const placeInput = widget.querySelector('[data-tpv20-weatherpilot-place]');
    const button = widget.querySelector('[data-tpv20-weatherpilot-fetch]');
    const source = widget.querySelector('[data-tpv20-weatherpilot-source]');
    const place = normalizePlace(placeInput?.value);

    if (button?.disabled) return;
    if (!place) {
      setStatus(widget, 'Bitte PLZ oder Ort eingeben.', 'error');
      placeInput?.focus();
      return;
    }
    if (!state.targetDate) {
      setStatus(widget, 'Bitte zuerst den Einsatztag wählen.', 'error');
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setStatus(widget, 'Wetter wird geladen …', 'loading');

    try {
      const location = await geocode(place, config);
      if (!location) {
        setStatus(widget, 'Ort nicht gefunden. Unterstützt werden Deutschland und direkte Nachbarländer. Bei ausländischen PLZ bitte den Ort ergänzen.', 'error');
        return;
      }

      const weather = await fetchWeather(location, state.targetDate, state.targetTime || '18:00', config);
      const temperature = toNumber(weather?.temperature);
      if (temperature === null) {
        setStatus(widget, 'Für diesen Termin liegt noch keine Prognose vor. Temperatur bitte manuell eingeben.', 'error');
        return;
      }

      const successPanel = root.querySelector('[data-tpv20-cart-success]:not([hidden])');
      const editButton = root.querySelector('[data-tpv20-success-edit]');
      if (successPanel && editButton) editButton.click();

      const currentTemperatureInput = findTemperatureInput(root) || state.temperatureInput;
      if (!currentTemperatureInput) {
        setStatus(widget, 'Temperaturfeld nicht gefunden. Bitte Temperatur manuell eingeben.', 'error');
        return;
      }

      const rounded = Math.round(temperature);
      state.syncing = true;
      clearTemperatureHighlight(currentTemperatureInput);
      currentTemperatureInput.value = String(rounded);
      currentTemperatureInput.dispatchEvent(new Event('input', { bubbles: true }));
      state.syncing = false;

      state.appliedTemperature = rounded;
      state.lastPlace = place;
      setLastPlace(place);
      markTemperatureApplied(findTemperatureInput(root) || currentTemperatureInput);
      if (source) source.textContent = weather.source || (location.countryCode === 'de' ? 'DWD' : 'Open-Meteo');

      const condition = conditionLabel(weather?.condition);
      setStatus(
        widget,
        `✓ ${location.label || place} · ${rounded} °C${condition ? ` · ${condition}` : ''} · ${formatTarget(state.targetDate, state.targetTime || '18:00')} übernommen`,
        'success'
      );
    } catch (error) {
      console.warn(`[TPV20 WetterPilot ${VERSION}] Wetterabruf fehlgeschlagen.`, error);
      if (/HTTP (400|404|422)/.test(String(error?.message || ''))) {
        setStatus(widget, 'Für diesen Termin liegt noch keine Prognose vor. Temperatur bitte manuell eingeben.', 'error');
      } else {
        setStatus(widget, 'Wetterdienst gerade nicht erreichbar. Manuelle Eingabe bleibt verfügbar.', 'error');
      }
    } finally {
      state.syncing = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  function mount(root, state) {
    syncTiming(root, state);

    if (!state.config.enabled || !isPlannerActive(root)) {
      state.widget?.remove();
      state.widget = null;
      state.temperatureInput = null;
      return;
    }

    const temperatureInput = findTemperatureInput(root);
    if (!temperatureInput) {
      state.widget?.remove();
      state.widget = null;
      state.temperatureInput = null;
      return;
    }

    const field = temperatureInput.closest('.tpv20-field') || temperatureInput.parentElement;
    if (state.widget?.isConnected && state.widget.nextElementSibling === field) {
      state.temperatureInput = temperatureInput;
      if (state.appliedTemperature !== null && Number(temperatureInput.value) === state.appliedTemperature) {
        markTemperatureApplied(temperatureInput);
      }
      return;
    }

    state.widget?.remove();
    createWidget(root, state, temperatureInput);
  }

  function scheduleMount(root, state) {
    if (state.queued) return;
    state.queued = true;
    requestAnimationFrame(() => {
      state.queued = false;
      mount(root, state);
    });
  }

  function initRoot(root) {
    if (!root || states.has(root)) return;
    const config = parseRootConfig(root);
    if (!config.enabled) return;

    const state = {
      config,
      targetDate: '',
      targetTime: '18:00',
      lastPlace: getLastPlace(),
      widget: null,
      temperatureInput: null,
      appliedTemperature: null,
      syncing: false,
      queued: false,
      observer: null
    };
    states.set(root, state);

    root.addEventListener('input', event => {
      syncTiming(root, state);
      if (event.target?.matches?.('[data-tpv20-field="temp"]') && event.isTrusted && !state.syncing) {
        state.appliedTemperature = null;
        clearTemperatureHighlight(event.target);
        if (state.widget) setStatus(state.widget, 'Temperatur manuell angepasst.', 'manual');
      }
      scheduleMount(root, state);
    }, true);

    root.addEventListener('change', () => {
      syncTiming(root, state);
      scheduleMount(root, state);
    }, true);

    root.addEventListener('click', () => {
      syncTiming(root, state);
      scheduleMount(root, state);
    }, true);

    state.observer = new MutationObserver(() => scheduleMount(root, state));
    state.observer.observe(root, { childList: true, subtree: true });
    scheduleMount(root, state);
  }

  function init(scope = document) {
    scope.querySelectorAll?.('[data-tpv20-root]').forEach(initRoot);
    if (scope.matches?.('[data-tpv20-root]')) initRoot(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => init(event.target || document));
})();
